import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  Typography,
  Box,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  AlertTitle,
  Stack,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  LinearProgress,
  Chip,
  Avatar,
} from "@mui/material";
import {
  CloudUpload,
  Description,
  Person,
  CheckCircle,
  Delete,
  UploadFile,
  Info,
  Warning,
  ArrowBack,
  Save,
  Visibility,
  Group,
  Article,
  Draw,
} from "@mui/icons-material";
import * as myApi from "../../components/Api";
import { toast } from "react-toastify";
import { showLoading } from "react-global-loading";
import ConfirmationDialog from "../../components/UI/ConfirmationDialog";
import {
  validateFile,
  validatePaperTitle,
  validateAuthorName,
  validatePhone,
} from "../../utils/formValidation";

export default function EditPaper() {
  const { paperCode } = useParams();
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingPaper, setLoadingPaper] = useState(true);
  const [paperData, setPaperData] = useState(null);

  const [paperType, setPaperType] = useState("General Paper");
  const [subTheme, setSubTheme] = useState("");
  const [category, setCategory] = useState("");
  const [participationType, setParticipationType] = useState("Individu");
  const [title, setTitle] = useState("");
  const [titleError, setTitleError] = useState("");

  const [authors, setAuthors] = useState([
    { name: "", email: "", phone: "", organization: "", address: "" },
  ]);

  const [files, setFiles] = useState({
    paper_file: null,
    anonymous_manuscript: null,
    cv_files: [],
    statement_file: null,
    pddikti_files: [],
    ktp_files: [],
  });
  const [fileErrors, setFileErrors] = useState({});
  const [existingFiles, setExistingFiles] = useState({
    paper_fileName_1: "",
    paper_filePath_1: "",
    anonymous_manuscript_fileName: "",
    anonymous_manuscript_filePath: "",
    cv_fileName_1: "",
    cv_filePath_1: "",
    cv_fileName_2: "",
    cv_filePath_2: "",
    cv_fileName_3: "",
    cv_filePath_3: "",
    statement_fileName_1: "",
    statement_filePath_1: "",
    pddikti_fileName_1: "",
    pddikti_filePath_1: "",
    pddikti_fileName_2: "",
    pddikti_filePath_2: "",
    pddikti_fileName_3: "",
    pddikti_filePath_3: "",
    ktp_fileName_1: "",
    ktp_filePath_1: "",
    ktp_fileName_2: "",
    ktp_filePath_2: "",
    ktp_fileName_3: "",
    ktp_filePath_3: "",
  });

  const [removeAuthorDialog, setRemoveAuthorDialog] = useState({ open: false, index: null, name: "" });

  const [subThemes, setSubThemes] = useState([]);
  const defaultSubThemes = [];

  const getDownloadUrl = (filePath) => {
    const baseUrl = window.env?.API_URL || process.env.REACT_APP_API_URL || "http://localhost:3001";
    return `${baseUrl}/${filePath}`;
  };

  const getExistingFileName = (prefix, index) => {
    const key = `${prefix}_fileName_${index + 1}`;
    return existingFiles[key] || existingFiles[`${prefix}_fileName_1`] || "";
  };

  const getExistingFilePath = (prefix, index) => {
    const key = `${prefix}_filePath_${index + 1}`;
    return existingFiles[key] || existingFiles[`${prefix}_filePath_1`] || "";
  };

  const renderFilePreviewLink = (prefix, index) => {
    const fileName = getExistingFileName(prefix, index);
    const filePath = getExistingFilePath(prefix, index);
    const currentFiles = files[`${prefix}_files`] || [];
    
    if (filePath && (!currentFiles || !currentFiles[index])) {
      return (
        <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
          <Typography 
            variant="caption" 
            sx={{ color: "primary.main", cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5 }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.open(getDownloadUrl(filePath), '_blank');
            }}
          >
            <Visibility sx={{ fontSize: 14 }} /> Preview
          </Typography>
        </Box>
      );
    }
    return null;
  };

  useEffect(() => {
    const fetchSubThemes = async () => {
      try {
        const response = await myApi.get("/sub-themes/");
        if (response.data && (response.data.result || response.data.data)) {
          const themes = response.data.result || response.data.data;
          setSubThemes(themes.map(t => typeof t === 'object' ? t.name : t));
        } else {
          setSubThemes(defaultSubThemes);
        }
      } catch (error) {
        console.error("Error fetching sub-themes:", error);
        setSubThemes(defaultSubThemes);
      }
    };
    fetchSubThemes();
    loadPaperData();
  }, [paperCode]);

  const loadPaperData = async () => {
    try {
      setLoadingPaper(true);
      const response = await myApi.post("/fetchPaper", {
        data_userid_code: paperCode,
        data_participation_type: "all",
      });

      if (response.data && response.data.result) {
        const paper = response.data.result;

        if (paper.submit_status === "submit") {
          const message = paper.paper_status === "lolos" 
            ? "Approved papers cannot be edited. Please contact admin if you need changes." 
            : "Submitted papers cannot be edited. Please contact admin if you need changes.";
          toast.error(message, {
            position: "top-right",
            autoClose: 5000,
          });
          navigate("/paper/list/");
          return;
        }

        setPaperData(paper);
        
        setPaperType(paper.paper_type || "General Paper");
        setSubTheme(paper.sub_theme || "");
        setCategory(paper.category || "");
        setParticipationType(paper.participation_type || "Individu");
        setTitle(paper.title || "");

        const loadedAuthors = [
          {
            name: paper.name_1 || "",
            email: paper.email_1 || "",
            phone: paper.phone_1 || "",
            organization: paper.organization_1 || "",
            address: paper.address_1 || "",
          },
        ];

        if (paper.name_2 && paper.name_2 !== '-' && paper.name_2 !== '') {
          loadedAuthors.push({
            name: paper.name_2 || "",
            email: paper.email_2 || "",
            phone: paper.phone_2 || "",
            organization: paper.organization_2 || "",
            address: paper.address_2 || "",
          });
        }

        if (paper.name_3 && paper.name_3 !== '-' && paper.name_3 !== '') {
          loadedAuthors.push({
            name: paper.name_3 || "",
            email: paper.email_3 || "",
            phone: paper.phone_3 || "",
            organization: paper.organization_3 || "",
            address: paper.address_3 || "",
          });
        }

        setAuthors(loadedAuthors);

        setExistingFiles({
          paper_fileName_1: paper.paper_fileName_1 || "",
          paper_filePath_1: paper.paper_filePath_1 || "",
          anonymous_manuscript_fileName: paper.anonymous_manuscript_fileName || "",
          anonymous_manuscript_filePath: paper.anonymous_manuscript_filePath || "",
          cv_fileName_1: paper.cv_fileName_1 || "",
          cv_filePath_1: paper.cv_filePath_1 || "",
          cv_fileName_2: paper.cv_fileName_2 || "",
          cv_filePath_2: paper.cv_filePath_2 || "",
          cv_fileName_3: paper.cv_fileName_3 || "",
          cv_filePath_3: paper.cv_filePath_3 || "",
          statement_fileName_1: paper.pernyataan_fileName_1 || "",
          statement_filePath_1: paper.pernyataan_filePath_1 || "",
          pddikti_fileName_1: paper.pddikti_fileName_1 || "",
          pddikti_filePath_1: paper.pddikti_filePath_1 || "",
          pddikti_fileName_2: paper.pddikti_fileName_2 || "",
          pddikti_filePath_2: paper.pddikti_filePath_2 || "",
          pddikti_fileName_3: paper.pddikti_fileName_3 || "",
          pddikti_filePath_3: paper.pddikti_filePath_3 || "",
          ktp_fileName_1: paper.ktp_fileName_1 || "",
          ktp_filePath_1: paper.ktp_filePath_1 || "",
          ktp_fileName_2: paper.ktp_fileName_2 || "",
          ktp_filePath_2: paper.ktp_filePath_2 || "",
          ktp_fileName_3: paper.ktp_fileName_3 || "",
          ktp_filePath_3: paper.ktp_filePath_3 || "",
        });

        setLoadingPaper(false);
      } else {
        toast.error("Failed to load paper data", {
          position: "top-right",
          autoClose: 5000,
        });
        navigate("/paper/list/");
      }
    } catch (error) {
      console.error("Error loading paper:", error);
      toast.error("Error loading paper data", {
        position: "top-right",
        autoClose: 5000,
      });
      navigate("/paper/list/");
    }
  };

  const validateStep = (step) => {
    if (step === 0) {
      const titleValidation = validatePaperTitle(title);
      setTitleError(titleValidation.valid ? "" : titleValidation.message);
      if (!titleValidation.valid) {
        toast(titleValidation.message, { type: "error" });
        return false;
      }
      if (!subTheme) {
        toast("Please select a sub-theme", { type: "error" });
        return false;
      }
      if (!category) {
        toast("Please select a category", { type: "error" });
        return false;
      }
      if (!participationType) {
        toast("Please select participation type", { type: "error" });
        return false;
      }
    } else if (step === 1) {
      for (let i = 0; i < authors.length; i++) {
        const author = authors[i];
        if (!author.name || !author.email || !author.phone || !author.organization) {
          toast(`Please fill all fields for Author ${i + 1}`, { type: "error" });
          return false;
        }
        const phoneValidation = validatePhone(author.phone);
        if (!phoneValidation.valid) {
          toast(phoneValidation.message, { type: "error" });
          return false;
        }
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleFileUpload = (event, fileType, index = null) => {
    const file = event.target.files[0];
    if (!file) return;

    const newFiles = { ...files };
    const newErrors = { ...fileErrors };

    if (fileType === "paper_file") {
      const maxSize = 5 * 1024 * 1024;
      const allowedTypes = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
      
      if (!allowedTypes.includes(file.type)) {
        newErrors.paper_file = "Only PDF and DOC files are allowed";
        setFileErrors(newErrors);
        return;
      }
      if (file.size > maxSize) {
        newErrors.paper_file = "File size must be less than 5MB";
        setFileErrors(newErrors);
        return;
      }
      newFiles.paper_file = file;
      delete newErrors.paper_file;
    } else if (fileType === "statement_file") {
      const maxSize = 1 * 1024 * 1024;
      if (file.type !== "application/pdf") {
        newErrors.statement_file = "Only PDF files are allowed";
        setFileErrors(newErrors);
        return;
      }
      if (file.size > maxSize) {
        newErrors.statement_file = "File size must be less than 1MB";
        setFileErrors(newErrors);
        return;
      }
      newFiles.statement_file = file;
      delete newErrors.statement_file;
    } else if (fileType === "cv_files") {
      const maxSize = 2 * 1024 * 1024;
      if (file.type !== "application/pdf") {
        newErrors[`cv_${index}`] = "Only PDF files are allowed";
        setFileErrors(newErrors);
        return;
      }
      if (file.size > maxSize) {
        newErrors[`cv_${index}`] = "File size must be less than 2MB";
        setFileErrors(newErrors);
        return;
      }
      newFiles.cv_files[index] = file;
      delete newErrors[`cv_${index}`];
    } else if (fileType === "pddikti_files") {
      if (file.type !== "application/pdf") {
        newErrors[`pddikti_${index}`] = "Only PDF files are allowed";
        setFileErrors(newErrors);
        return;
      }
      newFiles.pddikti_files[index] = file;
      delete newErrors[`pddikti_${index}`];
    } else if (fileType === "ktp_files") {
      const maxSize = 1 * 1024 * 1024;
      if (file.type !== "application/pdf") {
        newErrors[`ktp_${index}`] = "Only PDF files are allowed";
        setFileErrors(newErrors);
        return;
      }
      if (file.size > maxSize) {
        newErrors[`ktp_${index}`] = "File size must be less than 1MB";
        setFileErrors(newErrors);
        return;
      }
      newFiles.ktp_files[index] = file;
      delete newErrors[`ktp_${index}`];
    } else if (fileType === "anonymous_manuscript") {
      const maxSize = 5 * 1024 * 1024;
      if (file.type !== "application/pdf") {
        newErrors.anonymous_manuscript = "Only PDF files are allowed";
        setFileErrors(newErrors);
        return;
      }
      if (file.size > maxSize) {
        newErrors.anonymous_manuscript = "File size must be less than 5MB";
        setFileErrors(newErrors);
        return;
      }
      newFiles.anonymous_manuscript = file;
      delete newErrors.anonymous_manuscript;
    }

    setFiles(newFiles);
    setFileErrors(newErrors);
  };

  const handleSubmit = async () => {
    setLoading(true);
    showLoading(true);

    try {
      const formData = new FormData();
      formData.append("paper_code", paperCode);
      formData.append("jenis_paper_text", paperType);
      formData.append("sub_tema_text", subTheme);
      formData.append("sub_theme", subTheme);
      formData.append("kategori", category);
      formData.append("keikutsertaan", participationType);
      formData.append("judul", title);

      authors.forEach((author, index) => {
        formData.append(`name_${index + 1}`, author.name);
        formData.append(`email_${index + 1}`, author.email);
        formData.append(`phone_${index + 1}`, author.phone);
        formData.append(`organization_${index + 1}`, author.organization);
        formData.append(`address_${index + 1}`, author.address || '');
      });

      if (files.paper_file) {
        formData.append("paper_file", files.paper_file);
      }
      if (files.anonymous_manuscript) {
        formData.append("anonymous_manuscript", files.anonymous_manuscript);
      }
      if (files.statement_file) {
        formData.append("pernyataan_file", files.statement_file);
      }
      files.cv_files.forEach((cv, index) => {
        if (cv) {
          formData.append(index === 0 ? "cv_file" : `cv_${index + 1}_file`, cv);
        }
      });
      files.pddikti_files.forEach((pddikti, index) => {
        if (pddikti) {
          formData.append(index === 0 ? "pddikti_file" : `pddikti_${index + 1}_file`, pddikti);
        }
      });
      files.ktp_files.forEach((ktp, index) => {
        if (ktp) {
          formData.append(`ktp_file_${index + 1}`, ktp);
        }
      });

      const endpoint = participationType === "Group" ? "/updatePaperGroup" : "/updatePaperOne";
      
      const response = await myApi.post(endpoint, formData, "multipart/form-data");

      if (response.data && response.data.status === "success") {
        toast.success("Paper updated successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
        setTimeout(() => {
          navigate("/paper/list/");
        }, 1500);
      } else {
        toast.error(response.data?.message || "Failed to update paper", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error("Error updating paper:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.message || error.message || "Error updating paper", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
      showLoading(false);
    }
  };

  const steps = ["Paper Information", "Author Details", "File Uploads", "Review & Save"];

  const renderPaperInfo = () => (
    <Paper sx={{ p: 4, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Paper Information
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box>
          <FormControl component="fieldset">
            <FormLabel component="legend">Paper Type</FormLabel>
            <RadioGroup
              row
              value={paperType}
              onChange={(e) => setPaperType(e.target.value)}
            >
              <FormControlLabel
                value="General Paper"
                control={<Radio />}
                label="General Paper"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box>
          <FormControl fullWidth>
            <FormLabel>Sub-Theme</FormLabel>
            <select
              value={subTheme}
              onChange={(e) => setSubTheme(e.target.value)}
              style={{
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid rgba(0,0,0,0.23)",
                width: "100%",
                marginTop: "8px",
                backgroundColor: "#ffffff",
                color: "#1a1a1a",
                fontSize: "1rem",
              }}
            >
              <option value="">Select Sub-Theme</option>
              {subThemes.map((theme, index) => (
                <option key={index} value={theme}>
                  {theme}
                </option>
              ))}
            </select>
          </FormControl>
        </Box>

        <Box>
          <FormControl component="fieldset">
            <FormLabel component="legend">Category</FormLabel>
            <RadioGroup
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <FormControlLabel
                value="Umum"
                control={<Radio />}
                label="Umum (General)"
              />
              <FormControlLabel
                value="Mahasiswa"
                control={<Radio />}
                label="Mahasiswa (Student)"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box>
          <FormControl component="fieldset">
            <FormLabel component="legend">Participation Type</FormLabel>
            <RadioGroup
              value={participationType}
              onChange={(e) => setParticipationType(e.target.value)}
            >
              <FormControlLabel
                value="Individu"
                control={<Radio />}
                label="Individual"
              />
              <FormControlLabel
                value="Group"
                control={<Radio />}
                label="Group (2-3 Authors)"
              />
            </RadioGroup>
            {participationType === "Group" && (
              <Alert severity="info" sx={{ mt: 2 }} icon={<Info />}>
                <AlertTitle>Group Requirement</AlertTitle>
                Group participation requires <strong>at least 2 authors</strong>. You can add up to 3 authors total.
              </Alert>
            )}
          </FormControl>
        </Box>

        <Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Paper Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={!!titleError}
            helperText={titleError || `${title.length}/200 characters`}
            placeholder="Enter your paper title (minimum 10 characters)"
          />
        </Box>
      </Box>
    </Paper>
  );

  const renderAuthorDetails = () => (
    <Paper sx={{ p: 4, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Author Details
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {participationType === "Group" && authors.length < 2 && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<Warning />}>
          <AlertTitle>Minimum Authors Required</AlertTitle>
          Group participation requires at least <strong>2 authors</strong>. You currently have {authors.length}. 
          Please use the "Add Another Author" button below.
        </Alert>
      )}

      {authors.map((author, index) => (
        <Box key={index} sx={{ mb: 4 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Author {index + 1} {index === 0 ? "(Primary)" : ""}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={author.name}
              disabled={index === 0}
              onChange={(e) => {
                const newAuthors = [...authors];
                newAuthors[index].name = e.target.value;
                setAuthors(newAuthors);
              }}
              required
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={author.email}
              disabled={index === 0}
              onChange={(e) => {
                const newAuthors = [...authors];
                newAuthors[index].email = e.target.value;
                setAuthors(newAuthors);
              }}
              required
            />
            <TextField
              fullWidth
              label="Phone"
              value={author.phone}
              disabled={index === 0}
              onChange={(e) => {
                const newAuthors = [...authors];
                newAuthors[index].phone = e.target.value;
                setAuthors(newAuthors);
              }}
              required
            />
            <TextField
              fullWidth
              label="Organization"
              value={author.organization}
              disabled={index === 0}
              onChange={(e) => {
                const newAuthors = [...authors];
                newAuthors[index].organization = e.target.value;
                setAuthors(newAuthors);
              }}
              required
            />
            <TextField
              fullWidth
              label="Address"
              multiline
              rows={2}
              value={author.address}
              disabled={index === 0}
              onChange={(e) => {
                const newAuthors = [...authors];
                newAuthors[index].address = e.target.value;
                setAuthors(newAuthors);
              }}
            />
          </Box>
          {index > 0 && (
            <Button
              variant="text"
              color="error"
              startIcon={<Delete />}
              onClick={() => 
                setRemoveAuthorDialog({ 
                  open: true, 
                  index, 
                  name: author.name || `Author ${index + 1}` 
                })
              }
              sx={{ mt: 2 }}
            >
              Remove Author
            </Button>
          )}
        </Box>
      ))}

      {participationType === "Group" && authors.length < 3 && (
        <Button
          variant="outlined"
          onClick={() =>
            setAuthors([
              ...authors,
              { name: "", email: "", phone: "", organization: "", address: "" },
            ])
          }
          sx={{ mt: 2 }}
        >
          Add Another Author
        </Button>
      )}
    </Paper>
  );

  const renderFileUploads = () => (
    <Paper sx={{ p: 4, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        File Uploads
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Alert severity="info" sx={{ mb: 3, py: 1, px: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="caption" fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
            Requirements:
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Typography variant="caption" color="text.secondary">
              Paper: PDF/DOC (max 5MB)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Anonymous: PDF
            </Typography>
            <Typography variant="caption" color="text.secondary">
              CV: PDF (max 2MB each)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Statement: PDF (max 1MB)
            </Typography>
            {category === "Mahasiswa" && (
              <Typography variant="caption" color="text.secondary">
                PDDIKTI: PDF (per student)
              </Typography>
            )}
            {category === "Umum" && (
              <Typography variant="caption" color="text.secondary">
                KTP: PDF (per author)
              </Typography>
            )}
          </Box>
        </Box>
      </Alert>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 3 }}>
        <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 280 }}>
          <Box sx={{ border: 2, borderColor: "divider", borderRadius: 2, p: 3, textAlign: "center", cursor: "pointer", "&:hover": { borderColor: "primary.main" } }}>
            <input accept=".pdf,.doc,.docx" style={{ display: "none" }} id="paper-file" type="file" onChange={(e) => handleFileUpload(e, "paper_file")} />
            <label htmlFor="paper-file">
              <CloudUpload sx={{ fontSize: 48, color: "action.active", mb: 1 }} />
              <Typography variant="body1" fontWeight={600}>Paper File</Typography>
              <Typography variant="body2" color="text.secondary">
                {files.paper_file ? files.paper_file.name : existingFiles.paper_fileName_1 || "Click to upload"}
              </Typography>
              {fileErrors.paper_file && <Typography variant="caption" color="error">{fileErrors.paper_file}</Typography>}
              {existingFiles.paper_filePath_1 && !files.paper_file && (
                <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
                  <Typography variant="caption" sx={{ color: "primary.main", cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5 }} onClick={() => window.open(getDownloadUrl(existingFiles.paper_filePath_1), '_blank')}>
                    <Visibility sx={{ fontSize: 14 }} /> Preview
                  </Typography>
                </Box>
              )}
            </label>
          </Box>
        </Box>

        <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 280 }}>
          <Box sx={{ border: 2, borderColor: "divider", borderRadius: 2, p: 3, textAlign: "center", cursor: "pointer", "&:hover": { borderColor: "primary.main" } }}>
            <input accept=".pdf" style={{ display: "none" }} id="anonymous-manuscript-file" type="file" onChange={(e) => handleFileUpload(e, "anonymous_manuscript")} />
            <label htmlFor="anonymous-manuscript-file">
              <CloudUpload sx={{ fontSize: 48, color: "action.active", mb: 1 }} />
              <Typography variant="body1" fontWeight={600}>Anonymous Manuscript</Typography>
              <Typography variant="body2" color="text.secondary">
                {files.anonymous_manuscript ? files.anonymous_manuscript.name : existingFiles.anonymous_manuscript_fileName || "Click to upload"}
              </Typography>
              {fileErrors.anonymous_manuscript && <Typography variant="caption" color="error">{fileErrors.anonymous_manuscript}</Typography>}
              {existingFiles.anonymous_manuscript_filePath && !files.anonymous_manuscript && (
                <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
                  <Typography variant="caption" sx={{ color: "primary.main", cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5 }} onClick={() => window.open(getDownloadUrl(existingFiles.anonymous_manuscript_filePath), '_blank')}>
                    <Visibility sx={{ fontSize: 14 }} /> Preview
                  </Typography>
                </Box>
              )}
            </label>
          </Box>
        </Box>

        <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 280 }}>
          <Box sx={{ border: 2, borderColor: "divider", borderRadius: 2, p: 3, textAlign: "center", cursor: "pointer", "&:hover": { borderColor: "primary.main" } }}>
            <input accept=".pdf" style={{ display: "none" }} id="statement-file" type="file" onChange={(e) => handleFileUpload(e, "statement_file")} />
            <label htmlFor="statement-file">
              <CloudUpload sx={{ fontSize: 48, color: "action.active", mb: 1 }} />
              <Typography variant="body1" fontWeight={600}>Statement Letter</Typography>
              <Typography variant="body2" color="text.secondary">
                {files.statement_file ? files.statement_file.name : existingFiles.statement_fileName_1 || "Click to upload"}
              </Typography>
              {fileErrors.statement_file && <Typography variant="caption" color="error">{fileErrors.statement_file}</Typography>}
              {existingFiles.statement_filePath_1 && !files.statement_file && (
                <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
                  <Typography variant="caption" sx={{ color: "primary.main", cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5 }} onClick={() => window.open(getDownloadUrl(existingFiles.statement_filePath_1), '_blank')}>
                    <Visibility sx={{ fontSize: 14 }} /> Preview
                  </Typography>
                </Box>
              )}
            </label>
          </Box>
        </Box>
      </Box>

      {authors.map((author, index) => (
        <Box key={index} sx={{ mb: 4 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            Author {index + 1}: {author.name}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
            <Box sx={{ flex: 1, minWidth: 200, border: 2, borderColor: "divider", borderRadius: 2, p: 2, textAlign: "center", cursor: "pointer", "&:hover": { borderColor: "primary.main" } }}>
              <input accept=".pdf" style={{ display: "none" }} id={`cv-file-${index}`} type="file" onChange={(e) => handleFileUpload(e, "cv_files", index)} />
              <label htmlFor={`cv-file-${index}`}>
                <UploadFile sx={{ fontSize: 32, color: "action.active", mb: 1 }} />
                <Typography variant="body2">CV File</Typography>
                <Typography variant="caption" color="text.secondary">
                  {files.cv_files[index] ? files.cv_files[index].name : getExistingFileName('cv', index) || "Upload CV"}
                </Typography>
                {renderFilePreviewLink('cv', index)}
              </label>
            </Box>
            {category === "Mahasiswa" && (
              <Box sx={{ flex: 1, minWidth: 200, border: 2, borderColor: "divider", borderRadius: 2, p: 2, textAlign: "center", cursor: "pointer", "&:hover": { borderColor: "primary.main" } }}>
                <input accept=".pdf" style={{ display: "none" }} id={`pddikti-file-${index}`} type="file" onChange={(e) => handleFileUpload(e, "pddikti_files", index)} />
                <label htmlFor={`pddikti-file-${index}`}>
                  <UploadFile sx={{ fontSize: 32, color: "action.active", mb: 1 }} />
                  <Typography variant="body2">PDDIKTI Proof</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {files.pddikti_files[index] ? files.pddikti_files[index].name : getExistingFileName('pddikti', index) || "Upload PDDIKTI"}
                  </Typography>
                  {renderFilePreviewLink('pddikti', index)}
                </label>
              </Box>
            )}
            {category === "Umum" && (
              <Box sx={{ flex: 1, minWidth: 200, border: 2, borderColor: "divider", borderRadius: 2, p: 2, textAlign: "center", cursor: "pointer", "&:hover": { borderColor: "primary.main" } }}>
                <input accept=".pdf" style={{ display: "none" }} id={`ktp-file-${index}`} type="file" onChange={(e) => handleFileUpload(e, "ktp_files", index)} />
                <label htmlFor={`ktp-file-${index}`}>
                  <UploadFile sx={{ fontSize: 32, color: "action.active", mb: 1 }} />
                  <Typography variant="body2">KTP</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {files.ktp_files[index] ? files.ktp_files[index].name : getExistingFileName('ktp', index) || "Upload KTP"}
                  </Typography>
                  {renderFilePreviewLink('ktp', index)}
                </label>
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Paper>
  );

  const renderReview = () => (
    <Paper sx={{ p: 4, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Review & Save Changes
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Alert severity="warning" sx={{ mb: 3 }} icon={<Warning />}>
        <AlertTitle>Please Review Carefully</AlertTitle>
        Make sure all information is correct before saving. You can still edit after saving if paper is not submitted.
      </Alert>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Paper Title
        </Typography>
        <Typography variant="h6" fontWeight={600} sx={{ color: "text.primary", lineHeight: 1.4 }}>
          {title}
        </Typography>
      </Paper>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Info fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={600} color="primary">
              Paper Information
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="body2" color="text.secondary">Type</Typography>
              <Chip label={paperType} size="small" variant="soft" color="primary" />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="body2" color="text.secondary">Sub-Theme</Typography>
              <Typography variant="body2" fontWeight={500}>{subTheme}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="body2" color="text.secondary">Category</Typography>
              <Chip label={category === "Mahasiswa" ? "Student" : "General"} size="small" color={category === "Mahasiswa" ? "secondary" : "default"} />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}>
              <Typography variant="body2" color="text.secondary">Participation</Typography>
              <Chip label={participationType === "Individu" ? "Individual" : "Group"} size="small" variant="outlined" />
            </Box>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Group fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={600} color="primary">
              Authors ({authors.length})
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {authors.map((author, index) => (
              <Box key={index} sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "grey.50", border: 1, borderColor: "divider" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main", fontSize: 14 }}>
                    {author.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {index === 0 ? "Primary Author" : `Author ${index + 1}`}: {author.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {author.email}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>

      <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 3 }}>
        Uploaded Files
      </Typography>
      <Paper variant="outlined" sx={{ p: 0, borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, bgcolor: "grey.50" }}>
          <Box sx={{ p: 2, borderRight: 1, borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Article fontSize="small" color="primary" />
              <Typography variant="body2" fontWeight={600}>Paper File</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all", display: "block" }}>
              {files.paper_file ? files.paper_file.name : existingFiles.paper_fileName_1 || "—"}
            </Typography>
          </Box>
          <Box sx={{ p: 2, borderRight: 1, borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Description fontSize="small" color="primary" />
              <Typography variant="body2" fontWeight={600}>Anonymous</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all", display: "block" }}>
              {files.anonymous_manuscript ? files.anonymous_manuscript.name : existingFiles.anonymous_manuscript_fileName || "—"}
            </Typography>
          </Box>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Draw fontSize="small" color="primary" />
              <Typography variant="body2" fontWeight={600}>Statement</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all", display: "block" }}>
              {files.statement_file ? files.statement_file.name : existingFiles.statement_fileName_1 || "—"}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
        Author Documents
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {authors.map((author, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={600} color="primary" gutterBottom>
              {index === 0 ? "Primary Author" : `Author ${index + 1}`}: {author.name}
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mt: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <UploadFile fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">CV</Typography>
                  <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                    {files.cv_files[index] ? files.cv_files[index].name : getExistingFileName('cv', index) || "—"}
                  </Typography>
                </Box>
              </Box>
              {category === "Mahasiswa" && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Description fontSize="small" color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">PDDIKTI</Typography>
                    <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                      {files.pddikti_files[index] ? files.pddikti_files[index].name : getExistingFileName('pddikti', index) || "—"}
                    </Typography>
                  </Box>
                </Box>
              )}
              {category === "Umum" && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Description fontSize="small" color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">KTP</Typography>
                    <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                      {files.ktp_files[index] ? files.ktp_files[index].name : getExistingFileName('ktp', index) || "—"}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        ))}
      </Box>
    </Paper>
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return renderPaperInfo();
      case 1:
        return renderAuthorDetails();
      case 2:
        return renderFileUploads();
      case 3:
        return renderReview();
      default:
        return null;
    }
  };

  if (loadingPaper) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2, textAlign: "center" }}>
          Loading paper data...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate("/paper/list/")}
          sx={{ textTransform: "none" }}
        >
          Back to List
        </Button>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Edit Paper
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Paper Code: <strong>{paperCode}</strong>
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {renderStepContent(activeStep)}

      <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          onClick={handleBack}
          disabled={activeStep === 0 || loading}
        >
          Back
        </Button>
        <Stack direction="row" spacing={2}>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={loading ? null : <Save />}
              size="large"
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading}
              size="large"
            >
              Next
            </Button>
          )}
        </Stack>
      </Box>

      <ConfirmationDialog
        open={removeAuthorDialog.open}
        onClose={() => setRemoveAuthorDialog({ open: false, index: null, name: "" })}
        onConfirm={() => {
          setAuthors(authors.filter((_, i) => i !== removeAuthorDialog.index));
          setRemoveAuthorDialog({ open: false, index: null, name: "" });
        }}
        title="Remove Author"
        message={`Are you sure you want to remove "${removeAuthorDialog.name}" from the author list? This action cannot be undone.`}
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
      />
    </Container>
  );
}